import { useEffect, useRef, useState, useCallback } from "react";
import Header from "../../components/Header/Header";
import "./AbsentCabFollowUps.css";
import {
  searchAbsentCab,
  getAbsentCabFollowups,
  createAbsentCabFollowup,
  updateAbsentCabFollowup,
  markAbsentSmsSent,
} from "../../services/absentCabFollowupService";

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (date) => {
  if (!date) return "";

  const parts = date.split("-");

  if (parts.length !== 3) return date;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const convertDisplayDateToDate = (value) => {
  if (!value) return null;

  const parts = value.split("/");

  if (parts.length !== 3) return null;

  return new Date(
    Number(parts[2]),
    Number(parts[1]) - 1,
    Number(parts[0])
  );
};

export default function AbsentCabFollowUps({ embedded = false }) {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [cabLastFour, setCabLastFour] = useState("");
  const [cabDetails, setCabDetails] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showMessage, setShowMessage] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [headerVisible, setHeaderVisible] = useState(true);

  const pageRef = useRef(null);
  const lastScrollY = useRef(0);

  const showToast = useCallback((type, text) => {
    setMessageType(type);
    setMessage(text);
    setShowMessage(true);

    setTimeout(() => {
      setShowMessage(false);
    }, 3500);
  }, []);

  const handleScroll = useCallback(() => {
    const element = pageRef.current;

    if (!element) return;

    const currentY = element.scrollTop;

    if (currentY < 10) {
      setHeaderVisible(true);
    } else if (currentY > lastScrollY.current + 5) {
      setHeaderVisible(false);
    } else if (currentY < lastScrollY.current - 5) {
      setHeaderVisible(true);
    }

    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    if (embedded) {
      return undefined;
    }

    const element = pageRef.current;

    if (!element) return;

    element.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, embedded]);

  const searchCab = async () => {
    const value = cabLastFour.trim();

    if (!/^\d{4}$/.test(value)) {
      showToast(
        "error",
        "Please enter exactly 4 numerical digits."
      );
      return;
    }

    if (!selectedDate) {
      showToast(
        "error",
        "Please select the absent date."
      );
      return;
    }

    try {
      setSearching(true);

      const cab = await searchAbsentCab({
        cabLast4: value,
        absentDate: selectedDate,
      });

      if (!cab) {
        setCabDetails(null);
        showToast(
          "error",
          "No active Dispatch vehicle found."
        );
        return;
      }

      setCabDetails(cab);
      setRemarks("");
      setEditingId(null);
      setShowMessage(false);
    } catch (error) {
      setCabDetails(null);
      showToast(
        "error",
        error.message || "Unable to search cab."
      );
    } finally {
      setSearching(false);
    }
  };

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAbsentCabFollowups({
        fromDate,
        toDate,
      });

      const safeData = Array.isArray(data) ? data : [];

      setRecords(safeData);
      setFilteredRecords(safeData);
    } catch (error) {
      setRecords([]);
      setFilteredRecords([]);

      showToast(
        "error",
        error.message || "Unable to load follow-ups."
      );
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, showToast]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const updateAndSendSms = async () => {
    if (!selectedDate) {
      showToast(
        "error",
        "Please select the absent date."
      );
      return;
    }

    if (!cabDetails) {
      showToast(
        "error",
        "Please search the cab first."
      );
      return;
    }

    if (!remarks.trim()) {
      showToast(
        "error",
        "Please enter absent remarks."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        absentDate: selectedDate,
        vehicleId:
          cabDetails.vehicle_id ||
          cabDetails.vehicleId,
        vehicleAssignmentId:
          cabDetails.vehicle_assignment_id ||
          cabDetails.vehicleAssignmentId,
        supplierId:
          cabDetails.supplier_id ||
          cabDetails.supplierId,
        driverId:
          cabDetails.driver_id ||
          cabDetails.driverId,
        remarks: remarks.trim(),
      };

      if (!payload.vehicleId) {
        throw new Error(
          "Vehicle information is missing."
        );
      }

      if (!payload.vehicleAssignmentId) {
        throw new Error(
          "Vehicle assignment information is missing."
        );
      }

      if (editingId) {
        await updateAbsentCabFollowup(
          editingId,
          payload
        );

        showToast(
          "success",
          "Absent cab details updated successfully."
        );
      } else {
        await createAbsentCabFollowup(payload);

        showToast(
          "success",
          "Absent cab details added successfully."
        );
      }

      await loadRecords();
      clearForm();
    } catch (error) {
      showToast(
        "error",
        error.message || "Unable to save absent cab details."
      );
    } finally {
      setSaving(false);
    }
  };

  const editRecord = (record) => {
    const parts = String(record.date || "").split("/");

    if (parts.length === 3) {
      setSelectedDate(
        `${parts[2]}-${parts[1]}-${parts[0]}`
      );
    }

    const cabNumber = record.cabNumber || "";

    setCabLastFour(
      cabNumber.slice(-4)
    );

    setCabDetails({
      cabNumber: record.cabNumber,
      supplier: record.supplier,
      driver: record.driver,
      contacts: Array.isArray(record.contacts)
        ? record.contacts
        : [],
      vehicleType: record.vehicleType,
      year: record.year,
      vehicle_id: record.vehicleId,
      vehicleId: record.vehicleId,
      vehicle_assignment_id:
        record.vehicleAssignmentId,
      vehicleAssignmentId:
        record.vehicleAssignmentId,
      supplier_id: record.supplierId,
      supplierId: record.supplierId,
      driver_id: record.driverId,
      driverId: record.driverId,
    });

    setRemarks(record.remarks || "");
    setEditingId(record.id);
    setShowMessage(false);

    if (pageRef.current) {
      pageRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const sendSms = async (record) => {
    try {
      await markAbsentSmsSent(record.id);
      await loadRecords();

      showToast(
        "success",
        `SMS marked as sent for ${record.driver}.`
      );
    } catch (error) {
      showToast(
        "error",
        error.message || "Unable to mark SMS as sent."
      );
    }
  };

  const clearForm = () => {
    setCabLastFour("");
    setCabDetails(null);
    setRemarks("");
    setEditingId(null);
    setShowMessage(false);
  };

  const filterRecords = async () => {
    if (!fromDate || !toDate) {
      showToast(
        "error",
        "Please select both From Date and To Date."
      );
      return;
    }

    if (fromDate > toDate) {
      showToast(
        "error",
        "From Date cannot be later than To Date."
      );
      return;
    }

    await loadRecords();
  };

  const refreshRecords = async () => {
    const today = getToday();

    setFromDate(today);
    setToDate(today);

    try {
      setLoading(true);

      const data = await getAbsentCabFollowups({
        fromDate: today,
        toDate: today,
      });

      const safeData = Array.isArray(data) ? data : [];

      setRecords(safeData);
      setFilteredRecords(safeData);
    } catch (error) {
      setRecords([]);
      setFilteredRecords([]);

      showToast(
        "error",
        error.message || "Unable to refresh records."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!filteredRecords.length) {
      showToast(
        "error",
        "No records available for the selected date range."
      );
      return;
    }

    const headers = [
      "S.No",
      "Date",
      "Cab Number",
      "Supplier",
      "Driver",
      "Contact Numbers",
      "Vehicle Type",
      "Year",
      "Remarks",
      "Updated By",
      "Updated At",
    ];

    const rows = filteredRecords.map(
      (record, index) => [
        index + 1,
        record.date || "",
        record.cabNumber || "",
        record.supplier || "",
        record.driver || "",
        Array.isArray(record.contacts)
          ? record.contacts.join(" / ")
          : "",
        record.vehicleType || "",
        record.year || "",
        record.remarks || "",
        record.updatedBy || "",
        record.updatedAt || "",
      ]
    );

    const csv = [
      headers,
      ...rows,
    ]
      .map((row) =>
        row
          .map(
            (value) =>
              `"${String(value).replace(
                /"/g,
                '""'
              )}"`
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob(
      [csv],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;
    link.download =
      `Absent_Cab_Report_${fromDate}_to_${toDate}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    showToast(
      "success",
      "Absent cab report downloaded successfully."
    );
  };

  return (
    <div
      className={
        embedded
          ? "absent-layout embedded"
          : "absent-layout"
      }
    >
      <div className="absent-content">
        {!embedded && (
          <div
            className={`absent-header-wrapper ${
              headerVisible
                ? "absent-header-visible"
                : "absent-header-hidden"
            }`}
          >
            <Header />
          </div>
        )}

        <div
          className="absent-page-container"
          ref={pageRef}
        >
          <div className="absent-cab-page">
            {showMessage && (
              <div
                className={`absent-message ${
                  messageType === "error"
                    ? "absent-message-error"
                    : "absent-message-success"
                }`}
              >
                <span>
                  {messageType === "error"
                    ? "!"
                    : "✓"}
                </span>

                <p>{message}</p>

                <button
                  type="button"
                  onClick={() =>
                    setShowMessage(false)
                  }
                >
                  ×
                </button>
              </div>
            )}

            <section className="absent-card">
              <div className="absent-card-title">
                Add Absent Cab Details
              </div>

              <div className="absent-search-form">
                <div className="absent-form-group">
                  <label>
                    Date <span>*</span>
                  </label>

                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) =>
                      setSelectedDate(
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="absent-form-group absent-cab-search-group">
                  <label>
                    Cab Last 4 Digits{" "}
                    <span>*</span>
                  </label>

                  <input
                    type="text"
                    value={cabLastFour}
                    maxLength={4}
                    inputMode="numeric"
                    placeholder="Enter last 4 digits"
                    onChange={(e) =>
                      setCabLastFour(
                        e.target.value.replace(
                          /\D/g,
                          ""
                        )
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        searchCab();
                      }
                    }}
                  />

                </div>

                <button
                  type="button"
                  className="absent-primary-btn"
                  onClick={searchCab}
                  disabled={searching}
                >
                  {searching
                    ? "Searching..."
                    : "Search Cab"}
                </button>
              </div>
            </section>

            {cabDetails && (
              <section className="absent-card">
                <div className="absent-card-header">
                  <div className="absent-card-title">
                    Absent Cab Information
                  </div>

                  <div className="absent-card-header-actions">
                    <button
                      type="button"
                      className="absent-secondary-btn"
                      onClick={clearForm}
                    >
                      Clear
                    </button>

                    <button
                      type="button"
                      className="absent-primary-btn"
                      onClick={updateAndSendSms}
                      disabled={saving}
                    >
                      {saving
                        ? "Saving..."
                        : editingId
                        ? "Update & Send SMS"
                        : "Add & Send SMS"}
                    </button>
                  </div>
                </div>

                <div className="absent-cab-details">
                  <div className="absent-detail-item">
                    <span>Cab Number</span>

                    <strong className="absent-cab-number">
                      {cabDetails.cabNumber}
                    </strong>
                  </div>

                  <div className="absent-detail-item">
                    <span>
                      Supplier / Driver
                    </span>

                    <strong>
                      {cabDetails.supplier}
                    </strong>

                    <small>
                      {cabDetails.driver}
                    </small>
                  </div>

                  <div className="absent-detail-item">
                    <span>
                      Contact Number
                    </span>

                    <div className="absent-contact-list">
                      {(Array.isArray(
                        cabDetails.contacts
                      )
                        ? cabDetails.contacts
                        : []
                      ).map(
                        (contact) => (
                          <a
                            href={`tel:${contact}`}
                            key={contact}
                          >
                            {contact}
                          </a>
                        )
                      )}
                    </div>
                  </div>

                  <div className="absent-detail-item">
                    <span>
                      Vehicle Type
                    </span>

                    <strong>
                      {cabDetails.vehicleType}
                    </strong>
                  </div>

                  <div className="absent-detail-item">
                    <span>Year</span>

                    <strong>
                      {cabDetails.year}
                    </strong>
                  </div>
                </div>

                <div className="absent-remarks-section">
                  <div className="absent-remarks-wrapper">
                    <label>
                      Absent Remarks{" "}
                      <span>*</span>
                    </label>

                    <textarea
                      value={remarks}
                      maxLength={250}
                      placeholder="Enter absent remarks here..."
                      onChange={(e) =>
                        setRemarks(
                          e.target.value
                        )
                      }
                    />

                    <div className="absent-remarks-footer">
                      <span>
                        Maximum 250 characters
                      </span>

                      <span>
                        {remarks.length} / 250
                      </span>
                    </div>
                  </div>

                  <div className="absent-sms-preview">
                    <div className="absent-sms-preview-title">
                      SMS Preview
                    </div>

                    <p>
                      Dear{" "}
                      <strong>
                        {cabDetails.driver}
                      </strong>
                      , your cab{" "}
                      <strong>
                        {cabDetails.cabNumber}
                      </strong>{" "}
                      is marked absent on{" "}
                      <strong>
                        {formatDate(
                          selectedDate
                        )}
                      </strong>
                      .
                    </p>

                    <p>
                      Remarks:{" "}
                      <strong>
                        {remarks ||
                          "Your remarks will appear here"}
                      </strong>
                    </p>

                    <div className="absent-sms-signature">
                      - Srinivasa Fleet
                      Management Pro
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="absent-card absent-list-card">
              <div className="absent-list-header">
                <div className="absent-card-title">
                  Absent Cab Follow-ups List
                </div>

                <div className="absent-list-actions">
                  <button
                    type="button"
                    className="absent-download-btn"
                    onClick={downloadReport}
                  >
                    ↓ Download Report
                  </button>

                  <button
                    type="button"
                    className="absent-refresh-btn"
                    onClick={refreshRecords}
                  >
                    ↻ Refresh
                  </button>
                </div>
              </div>

              <div className="absent-filter-row">
                <div className="absent-filter-group">
                  <label>
                    From Date
                  </label>

                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="absent-filter-group">
                  <label>
                    To Date
                  </label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                  />
                </div>

                <button
                  type="button"
                  className="absent-filter-btn"
                  onClick={filterRecords}
                >
                  Filter
                </button>
              </div>

              <div className="absent-table-wrapper">
                <table className="absent-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Date</th>
                      <th>Cab Number</th>
                      <th>
                        Supplier / Driver
                      </th>
                      <th>
                        Contact Number
                      </th>
                      <th>
                        Vehicle Type
                      </th>
                      <th>Year</th>
                      <th>Remarks</th>
                      <th>Updated By</th>
                      <th>Updated At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="11"
                          className="absent-empty"
                        >
                          Loading absent cab follow-ups...
                        </td>
                      </tr>
                    ) : filteredRecords.length >
                      0 ? (
                      filteredRecords.map(
                        (record, index) => (
                          <tr
                            key={record.id}
                          >
                            <td>
                              {index + 1}
                            </td>

                            <td>
                              {record.date}
                            </td>

                            <td>
                              <strong className="absent-table-cab">
                                {
                                  record.cabNumber
                                }
                              </strong>
                            </td>

                            <td>
                              <div className="absent-table-person">
                                <strong>
                                  {
                                    record.supplier
                                  }
                                </strong>

                                <span>
                                  {
                                    record.driver
                                  }
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className="absent-table-contacts">
                                {(Array.isArray(
                                  record.contacts
                                )
                                  ? record.contacts
                                  : []
                                ).map(
                                  (contact) => (
                                    <a
                                      href={`tel:${contact}`}
                                      key={
                                        contact
                                      }
                                    >
                                      {
                                        contact
                                      }
                                    </a>
                                  )
                                )}
                              </div>
                            </td>

                            <td>
                              {
                                record.vehicleType
                              }
                            </td>

                            <td>
                              {record.year}
                            </td>

                            <td>
                              <span className="absent-table-remarks">
                                {
                                  record.remarks
                                }
                              </span>
                            </td>

                            <td>
                              {
                                record.updatedBy
                              }
                            </td>

                            <td>
                              {
                                record.updatedAt
                              }
                            </td>

                            <td>
                              <div className="absent-actions">
                                <button
                                  type="button"
                                  className="absent-edit-btn"
                                  title="Edit"
                                  onClick={() =>
                                    editRecord(
                                      record
                                    )
                                  }
                                >
                                  ✎
                                </button>

                                <button
                                  type="button"
                                  className="absent-sms-btn"
                                  title="Send SMS"
                                  onClick={() =>
                                    sendSms(
                                      record
                                    )
                                  }
                                >
                                  ➤
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="11"
                          className="absent-empty"
                        >
                          No absent cab
                          records found for
                          the selected date
                          range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="absent-table-footer">
                <span>
                  Showing{" "}
                  <strong>
                    {filteredRecords.length}
                  </strong>{" "}
                  entries
                </span>

                <div className="absent-pagination">
                  <button
                    type="button"
                    disabled
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    className="active"
                  >
                    1
                  </button>

                  <button
                    type="button"
                    disabled
                  >
                    ›
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}